/// <reference types="vite-env" />

import {isApiError, SimpleApiTyped} from './simpleApi'
import {runWhile, urlEncode, Ticker} from '../'

export function getS2aHostname(): string {
    const stage = import.meta.env.VITE_STAGE
    switch (stage) {
        case 'prod':
            return 'https://my.share2act.io'
        case 'test':
            return 'https://my.share2act-test.io'
        case 'dev':
        case 'local':
            return 'https://my.share2act-dev.io'
        default:
            throw new Error('Stage not defined')
    }
}

export class S2aTokens {
    public readonly clientId: string

    public constructor(
        public readonly access: string,
        public readonly id: string,
        public readonly refresh: string,
        public readonly expires: Date,
        public readonly cleanup: Date
    ) {
        try {
            this.clientId = JSON.parse(Buffer.from(access.split('.')[1], 'base64').toString('utf-8')).client_id || ''
        } catch {
            this.clientId = ''
        }
    }

    public static invalid = new S2aTokens('', '', '', new Date(0), new Date(0))

    public static deserialize(json: Tokens): S2aTokens {
        const access = json.accessToken
        const id = json.idToken
        const refresh = json.refreshToken
        const expires = Date.parse(json.expires ?? '')
        const cleanup = Date.parse(json.cleanupTimestamp ?? '')
        if (!access || !id || !refresh || isNaN(expires) || isNaN(cleanup)) {
            return S2aTokens.invalid
        }

        return new S2aTokens(access, id, refresh, new Date(expires), new Date(cleanup))
    }

    public get isValid() {
        return !!this.access
    }

    public toJSON(): {
        accessToken: string
        idToken: string
        refreshToken: string
        expires: string
        cleanupTimestamp: string
    } {
        return {
            accessToken: this.access,
            idToken: this.id,
            refreshToken: this.refresh,
            expires: this.expires.toISOString(),
            cleanupTimestamp: this.cleanup.toISOString()
        }
    }

    public shouldRecreateUser(safeDuration = 3600 /* 1 hour */): boolean {
        return this.cleanup.getTime() <= Date.now() + safeDuration * 1000
    }

    public shouldRefresh(safeDuration = 1200 /* 20 mins */): boolean {
        return this.expires.getTime() <= Date.now() + safeDuration * 1000
    }
}

type HttpMethod =
    | 'get'
    | 'post'
    | 'put'
    | 'del'
    | 'delete'
    | 'patch'
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DEL'
    | 'DELETE'
    | 'PATCH'

export class S2aProxy {
    public get tokens(): S2aTokens {
        return this._tokens
    }

    private _tokens: S2aTokens
    private ticker: Ticker
    private refreshing?: Promise<void>
    private recreating?: Promise<void>

    public constructor(public readonly customer: string) {
        try {
            this._tokens = S2aTokens.deserialize(this.load())
        } catch {
            this._tokens = S2aTokens.invalid
        }

        this.ticker = new Ticker(15 * 60 * 1000 /* 15 minutes */, async () => {
            if (!this.tokens.isValid) {
                return
            }
            await this.refreshOrRecreate()
        })

        this.ticker.start()
    }

    public dispose(): void {
        this.ticker.stop()
    }

    public async getS2aLink(service?: string): Promise<string> {
        const host = getS2aHostname()

        await this.refreshOrRecreate()

        return `${host}/${service || ''}#idToken=${this.tokens.id}&accessToken=${this.tokens.access}&refreshToken=${
            this.tokens.refresh
        }`
    }

    public call<T>(method: HttpMethod, path: string, body: T, gateway = 's2a'): Promise<any> {
        return this.callImpl(method, path, body, gateway, false)
    }

    public get<T>(path: string, body: T, gateway = 's2a'): Promise<any> {
        return this.callImpl('get', path, body, gateway, false)
    }

    public put<T>(path: string, body: T, gateway = 's2a'): Promise<any> {
        return this.callImpl('put', path, body, gateway, false)
    }

    public post<T>(path: string, body: T, gateway = 's2a'): Promise<any> {
        return this.callImpl('post', path, body, gateway, false)
    }

    public del<T>(path: string, body: T, gateway = 's2a'): Promise<any> {
        return this.callImpl('del', path, body, gateway, false)
    }

    private async callImpl(method: HttpMethod, path: string, body: any, gateway: string, skipTokenCheck: boolean) {
        if (!skipTokenCheck) {
            await this.refreshOrRecreate()
        }

        const headers = {
            Authorization: `Bearer ${this.tokens.id}`
        }
        method = method.toLowerCase() as Lowercase<HttpMethod>
        switch (method) {
            case 'delete':
                method = 'del'
                break
        }
        return await SimpleApiTyped[method](gateway, path, {body, headers})
    }

    public async refreshOrRecreate(): Promise<void> {
        if (this.tokens.shouldRecreateUser()) {
            await this.recreate()
        } else if (this.tokens.shouldRefresh()) {
            await this.refresh()
        }
    }

    public async refresh(): Promise<void> {
        if (!this.refreshing) {
            this.refreshing = this._refresh()
        }

        await this.refreshing
    }

    private async _refresh(): Promise<void> {
        try {
            const response = await SimpleApiTyped.post<RefreshResponse>('s2a', '/auth/refresh', {
                body: {
                    refreshToken: this.tokens.refresh,
                    clientId: this.tokens.clientId
                }
            })
            this._tokens = new S2aTokens(
                response.AccessToken,
                response.IdToken,
                this.tokens.refresh,
                new Date(Date.now() + response.ExpiresIn * 1000),
                this.tokens.cleanup
            )
            this.store()
        } catch (err: any) {
            if (isApiError(err) && err.response?.statusCode === 401) {
                await this.recreate()
            } else {
                throw err
            }
        } finally {
            this.refreshing = undefined
        }
    }

    public async recreate(): Promise<void> {
        if (!this.recreating) {
            this.recreating = this._recreate()
        }

        await this.recreating
    }

    private async _recreate(): Promise<void> {
        try {
            const response = await SimpleApiTyped.post<TokenInitResponse>(
                'iamOperations',
                urlEncode`/accounts/${this.customer}/tokens`,
                {
                    body: {}
                }
            )
            await runWhile(
                async () => {
                    const state = await SimpleApiTyped.get<StateResponse>(
                        'iamOperations',
                        urlEncode`/tokens/${response.stateToken}`
                    )

                    if (state.state === 'IN_PROGRESS') {
                        return true
                    }

                    if (state.state === 'SUCCESS' && state.tokens) {
                        this._tokens = S2aTokens.deserialize(state.tokens)
                        this.store()
                        return false
                    }

                    throw new Error(state.errorMessage || 'Unable to login to S2A')
                },
                1000,
                30,
                'Login to S2A timed out'
            )
        } finally {
            this.recreating = undefined
        }
    }

    private store() {
        sessionStorage.setItem(`s2a-token-exchange-${this.customer}`, JSON.stringify(this.tokens))
    }

    private load(): Tokens {
        try {
            return JSON.parse(sessionStorage.getItem(`s2a-token-exchange-${this.customer}`) || '')
        } catch {
            return {}
        }
    }
}

interface TokenInitResponse {
    stateToken: string
}

interface Tokens {
    idToken?: string
    accessToken?: string
    expires?: string
    cleanupTimestamp?: string
    refreshToken?: string
}

interface StateResponse {
    state: 'SUCCESS' | 'ERROR' | 'IN_PROGRESS'
    accountId: string
    tokens?: Tokens
    errorMessage?: string
}

interface RefreshResponse {
    AccessToken: string
    ExpiresIn: number
    IdToken: string
}
